import { apiClient } from '@/lib/api/client'
import type { ClientPurchaseOrder } from '@/types/ticket'

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
}

export type BillingClientOption = { id: number; name: string; code: string }

function mapPurchaseOrder(po: Record<string, unknown>): ClientPurchaseOrder {
  return {
    id: Number(po.id),
    client_id: Number(po.client_id),
    po_date: (po.po_date as string) ?? null,
    po_number: String(po.po_number ?? '').trim(),
    description: (po.description as string) ?? null,
    portfolio: (po.portfolio as string) ?? null,
    sales_person: (po.sales_person as string) ?? null,
    amount: po.amount != null ? Number(po.amount) : null,
    duration: (po.duration as string) ?? null,
    start_date: (po.start_date as string) ?? null,
    end_date: (po.end_date as string) ?? null,
  }
}

export const ClientService = {
  async getClientOptions(): Promise<BillingClientOption[]> {
    const response = await apiClient.get('/options/clients')
    const rows = Array.isArray(response.data) ? response.data : []
    return rows.map((row: BillingClientOption) => ({
      id: Number(row.id), name: row.name || row.code, code: row.code,
    })).filter((row: BillingClientOption) => row.id > 0 && Boolean(row.code))
  },
  async getClientPurchaseOrders(clientId: number): Promise<ApiResponse<ClientPurchaseOrder[]>> {
    try {
      const perPage = 500
      const baseParams = {
        client_id: clientId,
        per_page: perPage,
        order: 'start_date',
        sort: 'desc',
      }

      const firstResponse = await apiClient.get('/organization/client-purchase-orders', {
        params: { ...baseParams, page: 1 },
      })
      const firstBody = firstResponse.data ?? {}
      const firstPage = Array.isArray(firstBody.data) ? firstBody.data : []
      const lastPage = Math.max(1, Number(firstBody.meta?.last_page ?? 1))

      const pages = [firstPage]
      for (let page = 2; page <= lastPage; page += 1) {
        const pageResponse = await apiClient.get('/organization/client-purchase-orders', {
          params: { ...baseParams, page },
        })
        const pageBody = pageResponse.data ?? {}
        const pageRows = Array.isArray(pageBody.data) ? pageBody.data : []
        pages.push(pageRows)
      }

      const orders = pages
        .flat()
        .map((po: Record<string, unknown>) => mapPurchaseOrder(po))
        .filter((po) => po.po_number !== '')

      return { success: true, data: orders }
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } }; message?: string }
      return {
        success: false,
        message: ax.response?.data?.message ?? ax.message ?? 'Failed to load purchase orders',
        data: [],
      }
    }
  },
}
